import { useState, useCallback } from "react";
import { useApi } from "../hooks/useApi";
import SettingsCliTokens from "./SettingsCliTokens";
import SettingsActivity from "./SettingsActivity";

type Tab = "api-keys" | "cli-tokens" | "activity";

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  type: "ci" | "agent";
  createdBy: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function Settings() {
  const [tab, setTab] = useState<Tab>("api-keys");
  const { data, loading, refetch } = useApi<{ keys: ApiKey[] }>(
    tab === "api-keys" ? "/api/api-keys" : ""
  );
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<"ci" | "agent">("agent");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch("/api/api-keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newName.trim(), type: newType }),
      });
      if (!res.ok) throw new Error("Failed to create key");
      const json = await res.json();
      setCreatedKey(json.plainKey);
      setNewName("");
      refetch();
    } finally {
      setCreating(false);
    }
  }, [newName, newType, refetch]);

  const handleRevoke = useCallback(async (id: string) => {
    if (!confirm("Revoke this API key? This cannot be undone.")) return;
    setRevoking(id);
    try {
      await fetch(`/api/api-keys/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      refetch();
    } finally {
      setRevoking(null);
    }
  }, [refetch]);

  const handleCopy = useCallback(() => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [createdKey]);

  const activeKeys = data?.keys.filter((k) => !k.revokedAt) ?? [];
  const revokedKeys = data?.keys.filter((k) => k.revokedAt) ?? [];

  return (
    <div>
      <h1 style={{ fontSize: "var(--font-size-lg)", fontWeight: 700, color: "var(--color-primary)", marginBottom: "var(--space-md)" }}>
        Settings
      </h1>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid var(--color-border)", marginBottom: "var(--space-lg)" }}>
        {([
          { id: "api-keys", label: "API Keys" },
          { id: "cli-tokens", label: "CLI Tokens" },
          { id: "activity", label: "Activity" }
        ] as Array<{ id: Tab; label: string }>).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "10px 16px",
              border: "none",
              background: "transparent",
              borderBottom: tab === t.id ? "2px solid var(--color-primary)" : "2px solid transparent",
              marginBottom: -2,
              cursor: "pointer",
              fontSize: "var(--font-size-sm)",
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? "var(--color-primary)" : "var(--color-text-secondary)"
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "cli-tokens" && <SettingsCliTokens />}
      {tab === "activity" && <SettingsActivity />}
      {tab !== "api-keys" && null}

      {tab === "api-keys" && <>
      {/* Create new key */}
      <div style={{
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
        padding: "var(--space-lg)",
        marginBottom: "var(--space-xl)",
      }}>
        <h2 style={{ fontSize: "var(--font-size-md)", fontWeight: 600, marginBottom: "var(--space-md)" }}>
          Create API Key
        </h2>
        <div style={{ display: "flex", gap: "var(--space-sm)", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>Name</label>
            <input
              type="text"
              placeholder="e.g. mac-mini-office, ci-runner-prod"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--color-border)",
                fontSize: "var(--font-size-sm)",
                width: 280,
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>Type</label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value as "ci" | "agent")}
              style={{
                padding: "8px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--color-border)",
                fontSize: "var(--font-size-sm)",
              }}
            >
              <option value="agent">Agent (host daemon)</option>
              <option value="ci">CI (pipeline runner)</option>
            </select>
          </div>
          <button
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
            style={{
              padding: "8px 20px",
              borderRadius: "var(--radius-md)",
              border: "none",
              background: "var(--color-primary)",
              color: "#fff",
              fontSize: "var(--font-size-sm)",
              fontWeight: 600,
              cursor: creating || !newName.trim() ? "not-allowed" : "pointer",
              opacity: creating || !newName.trim() ? 0.5 : 1,
            }}
          >
            {creating ? "Creating..." : "Create Key"}
          </button>
        </div>

        {/* Show created key */}
        {createdKey && (
          <div style={{
            marginTop: "var(--space-md)",
            padding: "var(--space-md)",
            background: "#fef3cd",
            borderRadius: "var(--radius-md)",
            border: "1px solid #ffc107",
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4, fontSize: "var(--font-size-sm)" }}>
              Copy this key now — it will not be shown again
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
              <code style={{
                flex: 1,
                padding: "8px 12px",
                background: "#fff",
                borderRadius: "var(--radius-md)",
                fontSize: 13,
                fontFamily: "monospace",
                wordBreak: "break-all",
                border: "1px solid var(--color-border)",
              }}>
                {createdKey}
              </code>
              <button
                onClick={handleCopy}
                style={{
                  padding: "8px 16px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--color-border)",
                  background: copied ? "#d4edda" : "#fff",
                  fontSize: "var(--font-size-sm)",
                  cursor: "pointer",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {copied ? "Copied!" : "Copy"}
              </button>
              <button
                onClick={() => setCreatedKey(null)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--color-border)",
                  background: "#fff",
                  fontSize: "var(--font-size-sm)",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Active keys */}
      <div style={{
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
        padding: "var(--space-lg)",
        marginBottom: "var(--space-xl)",
      }}>
        <h2 style={{ fontSize: "var(--font-size-md)", fontWeight: 600, marginBottom: "var(--space-md)" }}>
          Active Keys ({activeKeys.length})
        </h2>
        {loading ? (
          <div style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)" }}>Loading...</div>
        ) : activeKeys.length === 0 ? (
          <div style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)" }}>No active API keys</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-sm)" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Name</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Key</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Type</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Created</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Last Used</th>
                <th style={{ textAlign: "right", padding: "8px 12px" }}></th>
              </tr>
            </thead>
            <tbody>
              {activeKeys.map((k) => (
                <tr key={k.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "10px 12px", fontWeight: 500 }}>{k.name}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <code style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{k.keyPrefix}...</code>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{
                      padding: "2px 8px",
                      borderRadius: "var(--radius-pill)",
                      fontSize: 11,
                      fontWeight: 600,
                      background: k.type === "agent" ? "var(--color-status-available-bg)" : "var(--color-status-busy-bg)",
                      color: k.type === "agent" ? "var(--color-status-available-text)" : "var(--color-status-busy-text)",
                    }}>
                      {k.type}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px", color: "var(--color-text-secondary)" }}>
                    {timeAgo(k.createdAt)}
                    {k.createdBy && <span style={{ marginLeft: 4, fontSize: 11 }}>by {k.createdBy.split("@")[0]}</span>}
                  </td>
                  <td style={{ padding: "10px 12px", color: "var(--color-text-secondary)" }}>
                    {timeAgo(k.lastUsedAt)}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}>
                    <button
                      onClick={() => handleRevoke(k.id)}
                      disabled={revoking === k.id}
                      style={{
                        padding: "4px 12px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid #dc4405",
                        background: "transparent",
                        color: "#dc4405",
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: revoking === k.id ? "not-allowed" : "pointer",
                      }}
                    >
                      {revoking === k.id ? "Revoking..." : "Revoke"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Revoked keys */}
      {revokedKeys.length > 0 && (
        <div style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-md)",
          boxShadow: "var(--shadow-card)",
          padding: "var(--space-lg)",
        }}>
          <h2 style={{ fontSize: "var(--font-size-md)", fontWeight: 600, marginBottom: "var(--space-md)", color: "var(--color-text-secondary)" }}>
            Revoked Keys ({revokedKeys.length})
          </h2>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-sm)", opacity: 0.6 }}>
            <tbody>
              {revokedKeys.map((k) => (
                <tr key={k.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "8px 12px" }}>{k.name}</td>
                  <td style={{ padding: "8px 12px" }}><code style={{ fontSize: 12 }}>{k.keyPrefix}...</code></td>
                  <td style={{ padding: "8px 12px" }}>{k.type}</td>
                  <td style={{ padding: "8px 12px", color: "var(--color-text-secondary)" }}>
                    Revoked {timeAgo(k.revokedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>}
    </div>
  );
}
